import React, { useRef, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Iconography,
  Table,
  Loader,
  useModal,
  DocusignViewer,
} from '@lucid/lego';
import type { TableColumn } from '@lucid/lego';
import {
  useGenerateDeliveryAcceptancePreview,
  useGenerateDeliveryAcceptanceSecondSigner,
} from '../../../../../../api/services/lupp/order/order';
import type {
  OrderDetailsResponse,
  ModuleFileReference,
} from '../../../../../../api/services/lupp/order/dto.order';
import { DOCUMENT_CATEGORY } from '../../../../../../constants/constants';
import {
  downloadSfdcFileFromFilehub,
  fetchSfdcFileBlob,
} from '../../../../../../api/services/lupp/fileUpload/fileUpload';
import type { GenerateDocumentsFormData } from './GenerateDocuments/GenerateDocuments';
import GenerateDocuments from './GenerateDocuments/GenerateDocuments';
import { useStore } from '../../../../../../store/store';
import { formatDate } from '../../../../../../utils/tools';
import styles from './DeliveryAcceptance.module.scss';

type ServiceCenterOption = {
  value: string;
  label: string;
};

export type DeliveryAcceptanceProps = {
  orderData: OrderDetailsResponse;
  isFinalQualityCheckCompleted: boolean;
  isInvoiceGenerated?: boolean;
  serviceCenterLocationOptions: ServiceCenterOption[];
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  onDocumentsGenerated?: () => void;
  onSigningComplete?: (serviceCenterLocation: string | null) => void;
  dataTestId?: string;
};

const DELIVERY_DOC_CATEGORIES = new Set<string>([
  DOCUMENT_CATEGORY.DELIVERY_ACCEPTANCE,
  DOCUMENT_CATEGORY.DUE_BILL,
]);

const DOCUMENT_DISPLAY_NAMES: Record<string, string> = {
  [DOCUMENT_CATEGORY.DELIVERY_ACCEPTANCE]: 'Delivery Acceptance',
  [DOCUMENT_CATEGORY.DUE_BILL]: 'Due Bill',
};

/**
 * Delivery Acceptance section handling document generation, DocuSign signing,
 * delivery acceptance/due bill table, and preferred service center selection.
 *
 * @component
 * @param {DeliveryAcceptanceProps} props - Component props
 * @returns {JSX.Element} Delivery Acceptance section
 *
 * @example
 * ```tsx
 * <DeliveryAcceptance orderData={orderData} theme="dark" labels={labels} />
 * ```
 */
const DeliveryAcceptance: React.FC<DeliveryAcceptanceProps> = ({
  orderData,
  isFinalQualityCheckCompleted,
  isInvoiceGenerated = true,
  serviceCenterLocationOptions,
  theme,
  labels,
  onDocumentsGenerated,
  onSigningComplete,
  dataTestId = 'delivery-acceptance',
}) => {
  const isSigningPersistedFromBackend =
    orderData.deliveryAcceptanceDetails?.signingStatus === 'COMPLETED';

  const [docusignUrl, setDocusignUrl] = useState<string | null>(null);
  const [generateDocsError, setGenerateDocsError] = useState<string | null>(null);
  const [documentsGenerated, setDocumentsGenerated] = useState(isSigningPersistedFromBackend);
  const [signingCompleted, setSigningCompleted] = useState(isSigningPersistedFromBackend);
  const [selectedServiceCenter, setSelectedServiceCenter] = useState<string | null>(null);
  const [selectedDueBillStatus, setSelectedDueBillStatus] = useState<string | null>(
    orderData.deliveryAcceptanceDetails?.status ?? null
  );
  const [isAdvisorSigner, setIsAdvisorSigner] = useState(false);
  const [isLoadingSecondSigner, setIsLoadingSecondSigner] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const envelopeIdRef = useRef<string | null>(null);
  const deliveryIdRef = useRef<string | null>(null);
  const generateDocsModal = useModal();
  const docusignModal = useModal();
  const invertedTheme = theme === 'dark' ? 'light' : 'dark';
  const userIdentity = useStore((state) => state.userIdentity);

  const { mutateAsync: generatePreview, isPending: isGeneratingDocuments } =
    useGenerateDeliveryAcceptancePreview();
  const { mutateAsync: generateSecondSigner } = useGenerateDeliveryAcceptanceSecondSigner();

  const handleGenerateDocuments = useCallback(
    async (data: GenerateDocumentsFormData) => {
      setGenerateDocsError(null);

      try {
        const response = await generatePreview({
          orderIdentifier: orderData.orderIdentifier,
          deliveryId: orderData.sfdcDeliveryIdentifier || '',
          status: data.openDueBillStatus as 'Nothing Owed' | 'Open',
          triggeredBy: {
            name: [userIdentity?.firstName, userIdentity?.lastName].filter(Boolean).join(' '),
            email: userIdentity?.emailAddress,
            userId: userIdentity?.userPortalId,
          },
          agencyInfo: {
            agencyName: userIdentity?.organizationName,
            agencyId: userIdentity?.businessOrganizationId,
          },
          buyerInformation: {
            name: orderData.customerName || undefined,
            email: orderData.customerEmail || undefined,
          },
          paymentDetails: {
            paymentType: orderData.preferredPaymentMethod || undefined,
            remainingAmountAtHandoff: orderData.amountDue ? Number(orderData.amountDue) : undefined,
          },
          deliveryInstructions: data.deliveryInstructions || null,
          generalNotes: data.generalNotes || null,
          serviceCenterLocation: data.serviceCenterLocation || null,
          signingStatus: 'IN_PROGRESS',
        });

        generateDocsModal.closeModal();
        setDocumentsGenerated(true);
        setSelectedServiceCenter(data.serviceCenterLocation || null);
        setSelectedDueBillStatus(data.openDueBillStatus || null);
        onDocumentsGenerated?.();

        envelopeIdRef.current = response?.envelopeId ?? null;
        deliveryIdRef.current = response?.deliveryId ?? null;

        const viewUrl = response?.viewUrl;
        if (viewUrl) {
          setIsAdvisorSigner(false);
          setDocusignUrl(viewUrl);
          docusignModal.openModal();
        }
      } catch {
        setGenerateDocsError(
          labels?.generateDocumentsError || 'Failed to generate documents. Please try again.'
        );
      }
    },
    [
      generatePreview,
      orderData,
      userIdentity,
      generateDocsModal,
      docusignModal,
      onDocumentsGenerated,
      labels,
    ]
  );

  const handleCloseDocusign = useCallback(() => {
    docusignModal.closeModal();
    setDocusignUrl(null);
  }, [docusignModal]);

  const handleDocusignMessage = useCallback(
    async (event: MessageEvent) => {
      const data = typeof event.data === 'string' ? event.data : event.data?.event;
      if (data !== 'signing_complete') return;

      if (!isAdvisorSigner && envelopeIdRef.current && deliveryIdRef.current) {
        setIsLoadingSecondSigner(true);

        try {
          const secondSignerResponse = await generateSecondSigner({
            orderId: orderData.orderIdentifier,
            deliveryId: deliveryIdRef.current,
            envelopeId: envelopeIdRef.current,
          });

          const secondViewUrl = secondSignerResponse?.viewUrl;
          if (secondViewUrl) {
            setIsAdvisorSigner(true);
            setDocusignUrl(secondViewUrl);
          }
        } catch {
          docusignModal.closeModal();
          setGenerateDocsError(
            labels?.secondSignerError || 'Failed to load second signer form. Please try again.'
          );
        } finally {
          setIsLoadingSecondSigner(false);
        }
        return;
      }

      setSigningCompleted(true);
      onSigningComplete?.(selectedServiceCenter);
      docusignModal.closeModal();
      setDocusignUrl(null);

      generatePreview({
        orderIdentifier: orderData.orderIdentifier,
        deliveryId: orderData.sfdcDeliveryIdentifier || '',
        status: (selectedDueBillStatus as 'Nothing Owed' | 'Open') || 'Nothing Owed',
        signingStatus: 'COMPLETED',
        triggeredBy: {
          name: [userIdentity?.firstName, userIdentity?.lastName].filter(Boolean).join(' '),
          email: userIdentity?.emailAddress,
          userId: userIdentity?.userPortalId,
        },
        serviceCenterLocation: selectedServiceCenter,
      });
    },
    [
      docusignModal,
      onSigningComplete,
      selectedServiceCenter,
      selectedDueBillStatus,
      isAdvisorSigner,
      generateSecondSigner,
      generatePreview,
      orderData.orderIdentifier,
      orderData.sfdcDeliveryIdentifier,
      labels,
      userIdentity,
    ]
  );

  const backendDeliveryDocs = useMemo<ModuleFileReference[]>(
    () =>
      (orderData.moduleFileReferenceDtoList ?? []).filter(
        (doc) => doc.documentCategory && DELIVERY_DOC_CATEGORIES.has(doc.documentCategory)
      ),
    [orderData.moduleFileReferenceDtoList]
  );

  const deliveryDocuments = useMemo<ModuleFileReference[]>(() => {
    if (backendDeliveryDocs.length > 0) return backendDeliveryDocs;

    if (signingCompleted) {
      const placeholders: ModuleFileReference[] = [
        {
          fileIdentifier: 'placeholder-delivery-acceptance',
          documentCategory: DOCUMENT_CATEGORY.DELIVERY_ACCEPTANCE,
          fileGenerationStatus: 'GENERATING',
        },
      ];
      if (selectedDueBillStatus === 'Open') {
        placeholders.push({
          fileIdentifier: 'placeholder-due-bill',
          documentCategory: DOCUMENT_CATEGORY.DUE_BILL,
          fileGenerationStatus: 'GENERATING',
        });
      }
      return placeholders;
    }

    return [];
  }, [backendDeliveryDocs, signingCompleted, selectedDueBillStatus]);

  const handleDownload = useCallback(
    async (doc: ModuleFileReference) => {
      const sfdcFileId = doc.sfdcFileIdentifier;
      const sfdcDeliveryId = orderData.sfdcDeliveryIdentifier;
      if (!sfdcFileId || !sfdcDeliveryId) return;

      setDownloadingId(sfdcFileId);
      try {
        await downloadSfdcFileFromFilehub(
          sfdcDeliveryId,
          sfdcFileId,
          doc.fileNameOriginal || 'document.pdf'
        );
      } finally {
        setDownloadingId(null);
      }
    },
    [orderData.sfdcDeliveryIdentifier]
  );

  const handleOpenDocument = useCallback(
    async (doc: ModuleFileReference) => {
      const sfdcFileId = doc.sfdcFileIdentifier;
      const sfdcDeliveryId = orderData.sfdcDeliveryIdentifier;
      if (!sfdcFileId || !sfdcDeliveryId) return;

      const { blob } = await fetchSfdcFileBlob(sfdcDeliveryId, sfdcFileId);
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    },
    [orderData.sfdcDeliveryIdentifier]
  );

  const deliveryDocColumns = useMemo<TableColumn<ModuleFileReference>[]>(
    () => [
      {
        key: 'documentCategory',
        title: labels?.deliveryDocColumnDocument || 'Document',
        width: '30%',
        sortable: true,
        render: (_value, row) => (
          <Typography
            variant="body"
            size="small"
            color={row.sfdcFileIdentifier ? 'link' : 'black'}
            theme={theme}
            onClick={() => row.sfdcFileIdentifier && handleOpenDocument(row)}
          >
            {DOCUMENT_DISPLAY_NAMES[row.documentCategory ?? ''] || row.documentCategory}
          </Typography>
        ),
      },
      {
        key: 'fileGenerationStatus',
        title: labels?.deliveryDocColumnStatus || 'Status',
        width: '30%',
        sortable: true,
        render: (_value, row) => (
          <Typography variant="body" size="small" color="black" theme={theme}>
            {row.fileGenerationStatus === 'GENERATED'
              ? labels?.generatedStatus || 'Generated'
              : row.fileGenerationStatus === 'GENERATING'
                ? labels?.generatingStatus || 'Generating'
                : row.fileGenerationStatus || '—'}
          </Typography>
        ),
      },
      {
        key: 'createdDatetime',
        title: labels?.deliveryDocColumnGenerated || 'Generated Date',
        width: '25%',
        sortable: true,
        render: (_value, row) => (
          <Typography variant="body" size="small" color="black" theme={theme}>
            {row.createdDatetime ? formatDate(row.createdDatetime) : '—'}
          </Typography>
        ),
      },
      {
        key: 'fileIdentifier',
        title: labels?.deliveryDocColumnAction || 'Action',
        width: '15%',
        sortable: false,
        render: (_value, row) =>
          row.sfdcFileIdentifier ? (
            <Button
              label={
                downloadingId === row.sfdcFileIdentifier
                  ? labels?.downloadingButton || 'Downloading...'
                  : labels?.downloadButton || 'Download'
              }
              size="small"
              type="secondary"
              color="neutral"
              theme={invertedTheme}
              onClick={() => handleDownload(row)}
              disabled={!!downloadingId}
              gaComponent="download-delivery-doc"
              dataTestId={`download-doc-${row.sfdcFileIdentifier}`}
              className={styles.downloadButton}
            />
          ) : null,
      },
    ],
    [labels, theme, invertedTheme, downloadingId, handleOpenDocument, handleDownload]
  );

  return (
    <Box data-testid={dataTestId} className={styles.root}>
      <Typography variant="subtitle" size="small" theme={theme} color="black">
        {labels?.deliveryAcceptanceTitle || 'Delivery Acceptance and Due Bill'}
      </Typography>

      {!isFinalQualityCheckCompleted && (
        <Box mt={16} className={styles.errorBox} data-testid="quality-check-error">
          <Box className={styles.errorHeader}>
            <Iconography
              category="global"
              name="information"
              color="error-1"
              theme={theme}
              dataTestId="quality-check-error-icon"
            />
            <Typography variant="body" size="medium" theme={theme} color="error-1">
              {labels?.qualityCheckNeededTitle || 'Final Quality Check Needed'}
            </Typography>
          </Box>
          <Typography variant="body" size="small" theme={theme} color="black">
            {labels?.qualityCheckNeededMessage ||
              'Please complete the final Quality Check form less than 72 hours before delivery date and submit to generate Delivery Acceptance and Due Bill documents.'}
          </Typography>
        </Box>
      )}

      {isFinalQualityCheckCompleted && !documentsGenerated && deliveryDocuments.length === 0 && (
        <Box mt={16}>
          <Typography variant="body" size="medium" theme={theme} color="black">
            {isInvoiceGenerated
              ? labels?.generateDocumentsPrompt || 'Generate documents to get started.'
              : labels?.generateDocumentsInvoiceRequired ||
                'Please generate a Final Invoice before generating delivery documents.'}
          </Typography>
        </Box>
      )}

      {deliveryDocuments.length > 0 && (
        <Box mt={16}>
          <Table
            columns={deliveryDocColumns}
            data={deliveryDocuments}
            theme={invertedTheme}
            selection={{ enabled: false }}
            pagination={{ enabled: false }}
            compact
            dataTestId="delivery-acceptance-table"
          />
        </Box>
      )}

      {(isGeneratingDocuments || isLoadingSecondSigner) && (
        <Box mt={16} mb={16} data-testid="delivery-acceptance-loader">
          <Loader theme={theme} />
        </Box>
      )}

      {isFinalQualityCheckCompleted && !signingCompleted && deliveryDocuments.length === 0 && (
        <Box mt={12}>
          <Button
            label={
              isGeneratingDocuments
                ? labels?.generatingDocumentsButton || 'Generating...'
                : labels?.generateDocumentsButton || 'Generate Documents'
            }
            size="medium"
            type="secondary"
            color="neutral"
            theme={invertedTheme}
            onClick={generateDocsModal.openModal}
            gaComponent="customer-handoff-generate-documents"
            dataTestId="generate-documents-button"
            disabled={
              !isFinalQualityCheckCompleted ||
              !isInvoiceGenerated ||
              isGeneratingDocuments ||
              isLoadingSecondSigner
            }
          />
        </Box>
      )}

      <GenerateDocuments
        errorMessage={generateDocsError}
        isOpen={generateDocsModal.isOpen}
        isSubmitting={isGeneratingDocuments}
        labels={labels}
        onClose={generateDocsModal.closeModal}
        onSubmit={handleGenerateDocuments}
        openDueBillStatusOptions={[
          { value: 'Nothing Owed', label: 'Nothing Owed' },
          { value: 'Open', label: 'Open' },
        ]}
        orderData={orderData}
        serviceCenterLocationOptions={serviceCenterLocationOptions}
        theme={invertedTheme}
      />

      {docusignUrl && (
        <DocusignViewer
          url={docusignUrl}
          dataTestId="delivery-acceptance-docusign-viewer"
          isOpen={docusignModal.isOpen}
          onClose={handleCloseDocusign}
          onMessage={handleDocusignMessage}
          theme={invertedTheme}
          title={
            isAdvisorSigner
              ? labels?.deliveryAcceptanceSecondSignerTitle || 'Delivery Advisor Signing'
              : labels?.deliveryAcceptanceDocusignTitle || 'Delivery Acceptance Document'
          }
        />
      )}
    </Box>
  );
};

DeliveryAcceptance.displayName = 'DeliveryAcceptance';

export default DeliveryAcceptance;
